import React, { useEffect, useState } from "react";
import { ArrowUpCircle, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { CRM_API, errMsg } from "../api";

// PPF partial → full upgrades on an order (estimate or invoice).
// Options and amounts come from the centralized PPF engine (vehicle class).
export default function PpfUpgradesPanel({ kind, docId, items, onChanged }) {
  const [options, setOptions] = useState([]);
  const [busy, setBusy] = useState("");

  const load = () =>
    CRM_API.get(`/${kind}/${docId}/ppf-upgrades`).then(({ data }) => setOptions(data.options || [])).catch(() => {});
  useEffect(() => { load(); }, [docId, JSON.stringify((items || []).map((i) => i.name))]); // eslint-disable-line

  const upgradeLines = (items || []).filter((it) => it.ppf_upgrade);
  if (options.length === 0 && upgradeLines.length === 0) return null;

  const add = async (key) => {
    setBusy(key);
    try {
      await CRM_API.post(`/${kind}/${docId}/ppf-upgrades`, { part_key: key });
      toast.success("Upgrade added — totals updated");
      onChanged();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(""); }
  };
  const remove = async (key) => {
    setBusy(key);
    try {
      await CRM_API.delete(`/${kind}/${docId}/ppf-upgrades/${key}`);
      toast.success("Upgrade removed — totals updated");
      onChanged();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(""); }
  };

  return (
    <div className="p-4 rounded-xl bg-amber-400/[0.05] border border-amber-400/20" data-testid="ppf-upgrades-panel">
      <div className="font-mono-alt text-[10px] text-amber-200/70 uppercase tracking-widest mb-2">
        PPF coverage upgrades — priced for this vehicle, difference only
      </div>
      <div className="space-y-1.5">
        {upgradeLines.map((it) => (
          <div key={it.ppf_key} className="flex items-center gap-3 text-sm" data-testid={`ppf-upg-line-${it.ppf_key}`}>
            <span className="flex-1 text-white/80">{it.name}</span>
            <span className="text-amber-200 font-semibold tabular-nums">+${Number(it.unit_price).toFixed(2)}</span>
            <button onClick={() => remove(it.ppf_key)} disabled={Boolean(busy)}
              data-testid={`ppf-upg-remove-${it.ppf_key}`}
              className="p-1.5 rounded text-white/40 hover:text-red-400 hover:bg-red-500/10">
              {busy === it.ppf_key ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
            </button>
          </div>
        ))}
        {options.map((o) => (
          <button key={o.key} onClick={() => add(o.key)} disabled={Boolean(busy)}
            data-testid={`ppf-upg-add-${o.key}`}
            className="w-full flex items-center gap-2 px-3 py-2 rounded-lg border border-dashed border-amber-400/40 text-amber-200/90 hover:border-amber-300/70 hover:bg-amber-400/10 text-sm transition-colors disabled:opacity-40">
            {busy === o.key ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ArrowUpCircle className="w-4 h-4" />}
            <span className="flex-1 text-left">↑ {o.label}</span>
            <span className="font-semibold tabular-nums">+${Number(o.amount).toFixed(2)}</span>
          </button>
        ))}
      </div>
    </div>
  );
}
